import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { Menu, Transition, Switch } from '@headlessui/react';
import { EllipsisHorizontalIcon, PencilIcon, TrashIcon, FolderIcon, GlobeAltIcon, DocumentIcon, ClockIcon, UserCircleIcon, UserIcon, Cog6ToothIcon as SettingsIcon, ArrowRightOnRectangleIcon as SignOutIcon, XMarkIcon, DocumentDuplicateIcon, AcademicCapIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import OfflineStatusBanner from '../OfflineStatusBanner/OfflineStatusBanner';
import { BasketIcon } from '../BasketIcon';
import { ShoppingBasket, Lemon } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import PublicTemplatesSection from './PublicTemplatesSection';
import PersonalWorkspacesSection from './PersonalWorkspacesSection';
import { usePremium } from '../Subscription/PremiumProvider';
import PremiumUpgradeModal from '../Subscription/PremiumUpgradeModal';
import OnboardingTour from '../OnboardingTour/OnboardingTour';

function WorkspaceView() {
  // State variables for user, workspaces, and UI controls
  const [user, setUser] = useState(null);
  const [personalWorkspaces, setPersonalWorkspaces] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [editingWorkspace, setEditingWorkspace] = useState(null);
  const [editedWorkspaceName, setEditedWorkspaceName] = useState('');

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [workspaceToDelete, setWorkspaceToDelete] = useState(null);
  const [isDeletingWorkspace, setIsDeletingWorkspace] = useState(false);

  const [publicWorkspaces, setPublicWorkspaces] = useState([]);
  const [isImporting, setIsImporting] = useState(false);

  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [workspaceToDuplicate, setWorkspaceToDuplicate] = useState(null);
  const [duplicateOptions, setDuplicateOptions] = useState({
    includeBaskets: true,
    includeItems: true
  });
  const [isDuplicating, setIsDuplicating] = useState(false);

  const [duplicateProgress, setDuplicateProgress] = useState(0);
  const [duplicateStatus, setDuplicateStatus] = useState('');

  const [importingWorkspaceId, setImportingWorkspaceId] = useState(null);

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const [showOnboardingTour, setShowOnboardingTour] = useState(false);
  const [userProfile, setUserProfile] = useState(null);

  const navigate = useNavigate();
  // Get both isPremium function and userProfile from PremiumProvider
  const { isPremium, getSubscriptionText, userProfile: premiumProfile, isCheckingStatus } = usePremium();

  // Simplified useEffect - only fetch user, not profile
  useEffect(() => {
    const fetchUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    };
    fetchUser();
  }, []);

  // Fetch user profile and check onboarding status
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) return;

      try {
        const { data: profile, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (error) throw error;

        setUserProfile(profile);
        
        // Show tour automatically if it hasn't been completed
        if (!profile.onboarding_tour_completed) {
          setShowOnboardingTour(true);
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };

    fetchUserProfile();
  }, [user]);

  // Fetch personal workspaces for the logged-in user
  useEffect(() => {
    const fetchWorkspaces = async () => {
      // Return early if no user is logged in
      if (!user) return;
      
      try {
        // Fetch workspaces from Supabase for the current user
        // Including a count of baskets for each workspace
        const { data: userWorkspaces, error: userError } = await supabase
          .from('workspaces')
          .select(`
            id,
            name,
            created_at,
            user_id,
            baskets:baskets(count)
          `)
          .eq('user_id', user.id) // Filter to only get current user's workspaces
          .order('created_at', { ascending: false }) // Most recent first
          .limit(20); // Limit to 20 workspaces

        // Throw any errors that occurred during fetch
        if (userError) throw userError;

        // Transform the data to add a basketCount property
        // This makes it easier to access the count in the UI
        const workspacesWithBasketCount = userWorkspaces.map(workspace => ({
          ...workspace,
          basketCount: workspace.baskets[0].count
        }));

        // Update state with the transformed workspaces
        setPersonalWorkspaces(workspacesWithBasketCount);
      } catch (error) {
        console.error('Error fetching workspaces:', error);
      }
    };

    // Call fetchWorkspaces whenever the user changes
    // This ensures we have the latest data when a user logs in
    if (user) {
      fetchWorkspaces();
    }
  }, [user]);

  // Fetch public workspaces
  useEffect(() => {
    const fetchPublicWorkspaces = async () => {
      if (!user) return;

      try {
        const { data: publicWorkspacesData, error } = await supabase
          .from('workspaces')
          .select(`
            id,
            name,
            description,
            created_at,
            updated_at,
            user_id,
            category,
            popularity_score,
            is_template,
            is_public,
            is_premium,
            download_count,
            baskets ( id ),
            topics ( id )
          `)
          .eq('is_public', true)
          .eq('is_template', true)
          .order('download_count', { ascending: false })
          .limit(6);

        if (error) throw error;

        // Format workspace data with new schema fields
        const formattedWorkspaces = publicWorkspacesData.map(workspace => ({
          ...workspace,
          basketCount: workspace.baskets?.length || 0,
          topicCount: workspace.topics?.length || 0,
          isPremium: workspace.is_premium || false,
          downloadCount: workspace.download_count || 0,
          rating: workspace.popularity_score ? (workspace.popularity_score / 20).toFixed(1) : 0
        }));

        setPublicWorkspaces(formattedWorkspaces);
      } catch (error) {
        console.error('Error fetching public workspaces:', error);
      }
    };

    if (user) {
      fetchPublicWorkspaces();
    }
  }, [user]);

  // Handle creating a new workspace
  const handleCreateWorkspace = async (e) => {
    e.preventDefault();
    if (!newWorkspaceName.trim()) return;

    setIsLoading(true);

    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      
      if (!currentUser) {
        throw new Error('No active session');
      }

      const { data, error } = await supabase
        .from('workspaces')
        .insert([{ 
          name: newWorkspaceName.trim(), 
          user_id: currentUser.id,
          is_public: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select();

      if (error) throw error;

      if (data && data.length > 0) {
        const newWorkspace = {
          ...data[0],
          basketCount: 0
        };
        
        setPersonalWorkspaces(prevWorkspaces => [newWorkspace, ...prevWorkspaces]);
        setNewWorkspaceName('');
        setShowForm(false);
      }
    } catch (error) {
      console.error('Error creating workspace:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle user logout
  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      // Redirect to the landing page
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error.message);
    }
  };

  // Handle workspace actions like edit and delete
  const handleWorkspaceAction = (workspace, action) => {
    switch (action) {
      case 'edit':
        setEditingWorkspace(workspace);
        setEditedWorkspaceName(workspace.name);
        break;
      case 'delete':
        setWorkspaceToDelete(workspace);
        setShowDeleteModal(true);
        break;
      default:
        break;
    }
  };

  // Save edited workspace name
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editedWorkspaceName.trim()) return;

    try {
      const { data, error } = await supabase
        .from('workspaces')
        .update({ 
          name: editedWorkspaceName.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('id', editingWorkspace.id)
        .select();

      if (error) throw error;

      if (data && data.length > 0) {
        setPersonalWorkspaces(prevWorkspaces =>
          prevWorkspaces.map(w => w.id === editingWorkspace.id ? 
            { ...data[0], basketCount: w.basketCount } : w
          )
        );
        setEditingWorkspace(null);
        setEditedWorkspaceName('');
      }
    } catch (error) {
      console.error('Error updating workspace:', error);
    }
  };

  // Handle workspace deletion
  const handleDeleteWorkspace = (workspace) => {
    setWorkspaceToDelete(workspace);
    setShowDeleteModal(true);
  };

  // Confirm workspace deletion
  const confirmDelete = async () => {
    if (!isDeletingWorkspace) {
      setIsDeletingWorkspace(true);
      return;
    }

    try {
      // Now proceed with workspace deletion (baskets will be automatically deleted due to CASCADE)
      const { error: workspaceError } = await supabase
        .from('workspaces')
        .delete()
        .eq('id', workspaceToDelete.id);

      if (workspaceError) throw workspaceError;

      setPersonalWorkspaces(prevWorkspaces =>
        prevWorkspaces.filter(w => w.id !== workspaceToDelete.id)
      );
      setShowDeleteModal(false);
      setIsDeletingWorkspace(false);
    } catch (error) {
      console.error('Error deleting workspace:', error);
      alert('Error deleting workspace. Please try again.');
    }
  };

  // Cancel workspace deletion
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setIsDeletingWorkspace(false);
  };

  // Navigate to a specific workspace
  const handleWorkspaceClick = (workspace) => {
    navigate(`/workspace/${workspace.id}`, { 
      state: { 
        workspaceName: workspace.name
      }
    });
  };

  // Handle importing a workspace
  const handleImportWorkspace = async (workspace) => {
    if (!user || isImporting || importingWorkspaceId) return;
    
    // Set both importing states
    setIsImporting(true);
    setImportingWorkspaceId(workspace.id);
    
    try {
      // First create a new workspace
      const { data: newWorkspace, error: workspaceError } = await supabase
        .from('workspaces')
        .insert([{
          name: `${workspace.name} (Imported)`,
          user_id: user.id,
          is_public: false,
          category: workspace.category,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (workspaceError) throw workspaceError;

      // Fetch all baskets, topics, and concepts from the original workspace
      const { data: originalBaskets, error: basketsError } = await supabase
        .from('baskets')
        .select('*')
        .eq('workspace_id', workspace.id)
        .order('order_index');

      if (basketsError) throw basketsError;

      // Create new baskets and keep track of old/new IDs
      const basketMapping = {};
      if (originalBaskets?.length > 0) {
        const newBaskets = originalBaskets.map(basket => ({
          name: basket.name,
          description: basket.description,
          workspace_id: newWorkspace.id,
          order_index: basket.order_index,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }));

        const { data: createdBaskets, error: createBasketsError } = await supabase
          .from('baskets')
          .insert(newBaskets)
          .select();

        if (createBasketsError) throw createBasketsError;

        // Create mapping of old basket IDs to new basket IDs
        originalBaskets.forEach((basket, index) => {
          basketMapping[basket.id] = createdBaskets[index].id;
        });
      }

      // Fetch all topics with their concepts and tags
      const { data: originalTopics, error: topicsError } = await supabase
        .from('topics')
        .select(`
          *,
          concepts (*),
          tags (*)
        `)
        .eq('workspace_id', workspace.id)
        .order('order_index');

      if (topicsError) throw topicsError;

      // Create new topics
      if (originalTopics?.length > 0) {
        // Handle tags first
        const tagsToCreate = originalTopics
          .filter(topic => topic.tags)
          .map(topic => ({
            name: topic.tags.name,
            color: topic.tags.color,
            workspace_id: newWorkspace.id,
            created_by: user.id,
            created_at: new Date().toISOString()
          }));

        const uniqueTags = tagsToCreate.filter((tag, index, self) =>
          index === self.findIndex((t) => t.name === tag.name)
        );

        let tagMapping = {};
        if (uniqueTags.length > 0) {
          const { data: createdTags, error: createTagsError } = await supabase
            .from('tags')
            .insert(uniqueTags)
            .select();

          if (createTagsError) throw createTagsError;

          // Create mapping of tag names to new tag IDs
          tagMapping = createdTags.reduce((acc, tag) => ({
            ...acc,
            [tag.name]: tag.id
          }), {});
        }

        // Create new topics with tag references
        const newTopics = originalTopics.map(topic => ({
          name: topic.name,
          basket_id: basketMapping[topic.basket_id],
          workspace_id: newWorkspace.id,
          user_id: user.id,
          tag_id: topic.tags ? tagMapping[topic.tags.name] : null,
          order_index: topic.order_index,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }));

        const { data: createdTopics, error: createTopicsError } = await supabase
          .from('topics')
          .insert(newTopics)
          .select();

        if (createTopicsError) throw createTopicsError;

        // Create mapping of old topic IDs to new topic IDs
        const topicMapping = {};
        originalTopics.forEach((topic, index) => {
          topicMapping[topic.id] = createdTopics[index].id;
        });

        // Create new concepts
        const allConcepts = originalTopics.flatMap(topic => 
          topic.concepts.map(concept => ({
            text: concept.text,
            topic_id: topicMapping[topic.id],
            is_highlighted: concept.is_highlighted,
            order_index: concept.order_index,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }))
        );

        if (allConcepts.length > 0) {
          const { error: createConceptsError } = await supabase
            .from('concepts')
            .insert(allConcepts);

          if (createConceptsError) throw createConceptsError;
        }
      }

      // Record the import
      const { error: importError } = await supabase
        .from('workspace_imports')
        .insert([{
          user_id: user.id,
          original_workspace_id: workspace.id,
          imported_workspace_id: newWorkspace.id,
          imported_at: new Date().toISOString()
        }]);

      if (importError) throw importError;
      // Increment the download count
      await supabase.rpc('increment_download_count', {
        workspace_id: workspace.id
      });

      // Add the new workspace to the list with the correct basket count
      const workspaceWithCount = {
        ...newWorkspace,
        basketCount: originalBaskets?.length || 0
      };
      
      setPersonalWorkspaces(prev => [workspaceWithCount, ...prev]);
      
      // Navigate to the new workspace
      navigate(`/workspace/${newWorkspace.id}`, {
        state: { workspaceName: newWorkspace.name }
      });
    } catch (error) {
      console.error('Error importing workspace:', error);
      alert('Failed to import workspace. Please try again.');
    } finally {
      // Clear both importing states
      setIsImporting(false);
      setImportingWorkspaceId(null);
    }
  };

  // Handle duplicating a workspace
  const handleDuplicateWorkspace = async (workspace, e) => {
    if (e) {
      e.stopPropagation();
    }
    
    setWorkspaceToDuplicate(workspace);
    setShowDuplicateModal(true);
  };

  // Confirm workspace duplication
  const confirmDuplicate = async () => {
    if (!workspaceToDuplicate || isDuplicating) return;
    
    setIsDuplicating(true);
    setDuplicateProgress(0);
    setDuplicateStatus('Creating new workspace...');
    
    try {
      // Create new workspace
      const { data: newWorkspace, error: workspaceError } = await supabase
        .from('workspaces')
        .insert([{
          name: `${workspaceToDuplicate.name} (Copy)`,
          user_id: user.id,
          is_public: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (workspaceError) throw workspaceError;
      
      setDuplicateProgress(20);

      // Always fetch all topics with their concepts and tags first
      setDuplicateStatus('Fetching topics and concepts...');
      const { data: originalTopics, error: topicsError } = await supabase
        .from('topics')
        .select(`
          *,
          concepts (*),
          tags (
            id,
            name,
            color
          )
        `)
        .eq('workspace_id', workspaceToDuplicate.id)
        .order('order_index');

      if (topicsError) throw topicsError;

      // Handle tags first
      setDuplicateStatus('Creating tags...');
      const tagsToCreate = originalTopics
        .filter(topic => topic.tags)
        .map(topic => ({
          name: topic.tags.name,
          color: topic.tags.color,
          workspace_id: newWorkspace.id,
          created_by: user.id,
          created_at: new Date().toISOString()
        }));

      const uniqueTags = tagsToCreate.filter((tag, index, self) =>
        index === self.findIndex((t) => t.name === tag.name)
      );

      let tagMapping = {};
      if (uniqueTags.length > 0) {
        const { data: createdTags, error: createTagsError } = await supabase
          .from('tags')
          .insert(uniqueTags)
          .select();

        if (createTagsError) throw createTagsError;

        tagMapping = createdTags.reduce((acc, tag) => ({
          ...acc,
          [tag.name]: tag.id
        }), {});
      }

      setDuplicateProgress(40);

      // If including baskets, create them first
      let basketMapping = {};
      if (duplicateOptions.includeBaskets) {
        setDuplicateStatus('Creating baskets...');
        const { data: originalBaskets, error: basketsError } = await supabase
          .from('baskets')
          .select('*')
          .eq('workspace_id', workspaceToDuplicate.id)
          .order('order_index');

        if (basketsError) throw basketsError;
        
        if (originalBaskets?.length > 0) {
          const newBaskets = originalBaskets.map(basket => ({
            name: basket.name,
            description: basket.description,
            workspace_id: newWorkspace.id,
            order_index: basket.order_index,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }));

          const { data: createdBaskets, error: createBasketsError } = await supabase
            .from('baskets')
            .insert(newBaskets)
            .select();

          if (createBasketsError) throw createBasketsError;

          basketMapping = originalBaskets.reduce((acc, basket, index) => ({
            ...acc,
            [basket.id]: createdBaskets[index].id
          }), {});
        }
      }

      setDuplicateProgress(60);

      // Create topics (with or without basket associations)
      if (originalTopics?.length > 0) {
        setDuplicateStatus('Creating topics...');
        const newTopics = originalTopics.map(topic => ({
          name: topic.name,
          basket_id: duplicateOptions.includeBaskets ? basketMapping[topic.basket_id] : null,
          workspace_id: newWorkspace.id,
          user_id: user.id,
          tag_id: topic.tags ? tagMapping[topic.tags.name] : null,
          order_index: topic.order_index,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }));

        const { data: createdTopics, error: createTopicsError } = await supabase
          .from('topics')
          .insert(newTopics)
          .select();

        if (createTopicsError) throw createTopicsError;

        setDuplicateProgress(80);
        setDuplicateStatus('Creating concepts...');

        // Create concepts for each topic
        const topicMapping = {};
        originalTopics.forEach((oldTopic, index) => {
          topicMapping[oldTopic.id] = createdTopics[index].id;
        });

        const allConcepts = originalTopics.flatMap(topic => 
          topic.concepts.map(concept => ({
            text: concept.text,
            topic_id: topicMapping[topic.id],
            is_highlighted: concept.is_highlighted,
            order_index: concept.order_index,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }))
        );

        if (allConcepts.length > 0) {
          const { error: createConceptsError } = await supabase
            .from('concepts')
            .insert(allConcepts);

          if (createConceptsError) throw createConceptsError;
        }
      }

      setDuplicateStatus('Finishing up...');
      setDuplicateProgress(100);

      // Add the new workspace to the list
      const workspaceWithCount = {
        ...newWorkspace,
        basketCount: duplicateOptions.includeBaskets ? workspaceToDuplicate.basketCount : 0
      };
      
      setPersonalWorkspaces(prev => [workspaceWithCount, ...prev]);
      
      // Navigate to the new workspace
      navigate(`/workspace/${newWorkspace.id}`, {
        state: { workspaceName: newWorkspace.name }
      });

      // Clear status and progress before closing
      setTimeout(() => {
        setDuplicateStatus('');
        setDuplicateProgress(0);
        setShowDuplicateModal(false);
        setWorkspaceToDuplicate(null);
        setDuplicateOptions({ includeBaskets: true, includeItems: true });
      }, 500);

    } catch (error) {
      console.error('Error duplicating workspace:', error);
      setDuplicateStatus('Error: ' + error.message);
      alert('Failed to duplicate workspace. Please try again.');
    } finally {
      setIsDuplicating(false);
    }
  };

  // Add this function to handle reopening the tour
  const handleReopenTour = () => {
    setShowOnboardingTour(true);
  };

  // Show loading state while checking subscription
  if (isCheckingStatus) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-neutral-200 border-t-cranberry-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <header className="bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between">
          {/* Logo/Brand with BasketIcon */}
          <div className="flex items-center">
            <BasketIcon className="h-6 w-6 sm:h-8 sm:w-8" />
            <h1 className="text-lg sm:text-2xl font-bold text-neutral-900 ml-2">Basket</h1>
          </div>

          {/* Subscription Status Badge */}
          <div className="flex items-center">
            <span className={`px-3 py-1 rounded-full text-sm font-medium border-2 border-neutral-900 ${
              isPremium() 
                ? 'bg-emerald-500 text-white'
                : 'bg-butter-500 text-white'
            }`}>
              {isPremium() ? 'Premium' : 'Basic'}
            </span>
          </div>

          {/* Header Actions - User Profile */}
          <div className="flex items-center">
            <Menu as="div" className="relative">
              <Menu.Button className="p-1 sm:p-1.5 hover:bg-neutral-100 rounded-xl transition-colors">
                <UserCircleIcon className="h-6 w-6 sm:h-7 sm:w-7 text-neutral-600" />
              </Menu.Button>

              <Transition
                as={React.Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 mt-2 w-64 origin-top-right bg-white rounded-xl border-2 border-neutral-900 shadow-lg">
                  {/* User Info Section */}
                  <div className="px-4 py-4 border-b-2 border-neutral-200">
                    <p className="text-sm font-medium text-neutral-900 mb-1">Profile</p>
                    <p className="text-xs text-neutral-600 truncate mb-3">
                      {user?.email}
                    </p>
                    
                    {/* Subscription Details */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        {isPremium() && (
                          <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-white text-xs font-medium border-2 border-neutral-900 bg-emerald-500">
                            Premium Plan
                          </span>
                        )}
                        {!isPremium() && (
                          <button 
                            className="px-4 py-1.5 rounded-lg bg-cranberry-700 text-white text-xs font-medium border-2 border-neutral-900 hover:bg-cranberry-600 transition-colors"
                            onClick={() => setShowUpgradeModal(true)}
                          >
                            Upgrade to Premium
                          </button>
                        )}
                      </div>
                      
                      {/* Show plan details */}
                      <div className="text-xs text-neutral-600">
                        <p>{getSubscriptionText()}</p>
                        {isPremium() && (
                          <p className="mt-1">
                            Plan ends: {premiumProfile?.subscription_end_date
                              ? new Date(premiumProfile.subscription_end_date).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long', 
                                  day: 'numeric'
                                })
                              : 'N/A'
                            }
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="py-2">
                    <Menu.Item>
                      {({ active }) => (
                        <button 
                          onClick={handleReopenTour}
                          className={`${
                            active ? 'bg-neutral-100' : ''
                          } flex items-center w-full px-4 py-3 text-sm text-neutral-900 group`}
                        >
                          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 mr-3 group-hover:bg-amber-200 transition-colors">
                            <AcademicCapIcon className="h-5 w-5 text-amber-800" />
                          </div>
                          <div className="flex flex-col items-start">
                            <span className="font-medium">How to use Basket</span>
                            <span className="text-xs text-neutral-500">Take a quick tour</span>
                          </div>
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button 
                          onClick={handleLogout}
                          className={`${
                            active ? 'bg-neutral-100' : ''
                          } flex items-center w-full px-4 py-2.5 text-sm text-cranberry-700`}
                        >
                          <SignOutIcon className="h-4 w-4 mr-3 text-cranberry-700" />
                          Sign out
                        </button>
                      )}
                    </Menu.Item>
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        </div>
      </header>

      <OfflineStatusBanner />

      {/* Updated Main Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        <PublicTemplatesSection
          workspaces={publicWorkspaces}
          onImport={handleImportWorkspace}
          importingWorkspaceId={importingWorkspaceId}
        />

        <PersonalWorkspacesSection
          workspaces={personalWorkspaces}
          onCreateWorkspace={() => setShowForm(true)}
          onEditWorkspace={(workspace) => handleWorkspaceAction(workspace, 'edit')}
          onDeleteWorkspace={(workspace) => handleWorkspaceAction(workspace, 'delete')}
          onDuplicateWorkspace={handleDuplicateWorkspace}
          onWorkspaceClick={handleWorkspaceClick}
          editingWorkspace={editingWorkspace}
          editedWorkspaceName={editedWorkspaceName}
          onEditNameChange={setEditedWorkspaceName}
          onSaveEdit={handleSaveEdit}
          isLoading={isLoading}
        />
      </main>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl border-2 border-neutral-900 shadow-lg max-w-sm w-full mx-4">
            <div className="px-6 py-4 border-b-2 border-neutral-200">
              <h3 className="text-lg font-semibold text-neutral-900">Delete Workspace</h3>
            </div>
            
            <div className="px-6 py-4">
              {!isDeletingWorkspace ? (
                <p className="text-neutral-600">
                  Are you sure you want to delete "{workspaceToDelete?.name}"?
                </p>
              ) : (
                <p className="text-cranberry-700 font-medium">
                  This will permanently delete all associated baskets and topics. This action cannot be undone.
                </p>
              )}
            </div>

            <div className="px-6 py-4 bg-neutral-50 rounded-b-xl border-t-2 border-neutral-200 flex justify-end space-x-3">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 text-neutral-900 hover:text-cranberry-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className={`px-4 py-2 rounded-xl border-2 border-neutral-900 font-medium
                  ${isDeletingWorkspace 
                    ? 'bg-cranberry-700 text-white hover:bg-cranberry-600' 
                    : 'bg-neutral-100 text-neutral-900 hover:bg-neutral-200'
                  } transition-colors`}
              >
                {isDeletingWorkspace ? 'Confirm Delete' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Workspace Form Modal - Simplified */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl border-2 border-neutral-900 shadow-lg max-w-md w-full">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b-2 border-neutral-200 flex justify-between items-center">
              <h3 className="text-xl font-semibold text-neutral-900">Create New Workspace</h3>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="p-1.5 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="h-5 w-5 text-neutral-600" />
              </button>
            </div>

            {/* Modal Body - Simplified */}
            <form onSubmit={handleCreateWorkspace}>
              <div className="px-6 py-6">
                <input
                  type="text"
                  id="workspaceName"
                  value={newWorkspaceName}
                  onChange={(e) => setNewWorkspaceName(e.target.value)}
                  placeholder="e.g. USMLE, PLAB, Finals"
                  className="w-full px-4 py-3 rounded-xl border-2 border-neutral-900 focus:border-cranberry-700 focus:ring-1 focus:ring-cranberry-700 outline-none transition-colors text-base"
                  autoFocus
                />
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 bg-neutral-50 rounded-b-xl border-t-2 border-neutral-200 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2.5 text-neutral-900 hover:text-cranberry-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading || !newWorkspaceName.trim()}
                  className={`px-5 py-2.5 bg-cranberry-700 text-white rounded-xl border-2 border-neutral-900 font-medium 
                    ${isLoading || !newWorkspaceName.trim() 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:bg-cranberry-600 transition-colors'}`}
                >
                  {isLoading ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDuplicateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl border-2 border-neutral-900 shadow-lg max-w-md w-full">
            <div className="px-6 py-4 border-b-2 border-neutral-200 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold text-neutral-900">Duplicate Workspace</h3>
                <p className="text-sm text-neutral-600 mt-1">
                  {workspaceToDuplicate?.name}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowDuplicateModal(false);
                  setWorkspaceToDuplicate(null);
                  setDuplicateOptions({ includeBaskets: true, includeItems: true });
                }}
                className="p-1.5 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="h-5 w-5 text-neutral-600" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <div className="text-sm font-medium text-neutral-900">Include baskets</div>
                    <div className="text-xs text-neutral-600">
                      {duplicateOptions.includeBaskets 
                        ? "Premade baskets with topics inside"
                        : "No baskets - create your own basket structure"}
                    </div>
                  </div>
                  <Switch
                    checked={duplicateOptions.includeBaskets}
                    onChange={(checked) => setDuplicateOptions({
                      includeBaskets: checked,
                      includeItems: true
                    })}
                    className={`${
                      duplicateOptions.includeBaskets ? 'bg-emerald-600' : 'bg-neutral-200'
                    } relative inline-flex h-6 w-11 items-center rounded-full border-2 border-neutral-900 transition-colors focus:outline-none`}
                  >
                    <span className={`${
                      duplicateOptions.includeBaskets ? 'translate-x-6' : 'translate-x-1'
                    } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`} />
                  </Switch>
                </div>

                <div className="flex items-center justify-between opacity-50">
                  <div className="space-y-1">
                    <div className="text-sm font-medium text-neutral-900">Include items</div>
                    <div className="text-xs text-neutral-600">All topics will be copied</div>
                  </div>
                  <Switch
                    checked={true}
                    disabled={true}
                    className="bg-emerald-600 relative inline-flex h-6 w-11 items-center rounded-full border-2 border-neutral-900 transition-colors focus:outline-none"
                  >
                    <span className="translate-x-6 inline-block h-4 w-4 transform rounded-full bg-white transition-transform" />
                  </Switch>
                </div>
              </div>

              {isDuplicating && (
                <div className="space-y-2 pt-2">
                  <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-cranberry-500 transition-all duration-300 ease-out"
                      style={{ width: `${duplicateProgress}%` }}
                    />
                  </div>
                  <p className="text-sm text-neutral-600 text-center">
                    {duplicateStatus}
                  </p>
                </div>
              )}
            </div>

            <div className="px-6 py-4 bg-neutral-50 rounded-b-xl border-t-2 border-neutral-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDuplicateModal(false);
                  setWorkspaceToDuplicate(null);
                  setDuplicateOptions({ includeBaskets: true, includeItems: true });
                }}
                className="px-4 py-2 text-neutral-900 hover:text-cranberry-700 transition-colors"
                disabled={isDuplicating}
              >
                Cancel
              </button>
              <button
                onClick={confirmDuplicate}
                disabled={isDuplicating}
                className="px-5 py-2 bg-cranberry-700 text-white rounded-xl border-2 border-neutral-900 font-medium hover:bg-cranberry-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDuplicating ? 'Duplicating...' : 'Duplicate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Premium Upgrade Modal */}
      <PremiumUpgradeModal 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)} 
      />

      {/* Add the OnboardingTour component */}
      {user && (
        <OnboardingTour
          isOpen={showOnboardingTour}
          onClose={() => setShowOnboardingTour(false)}
          userId={user.id}
        />
      )}
    </div>
  );
}

export default WorkspaceView;
